import { useState } from "react";
import PropTypes from "prop-types";
import { Check, Copy, KeyRound, TriangleAlert } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Shows a newly created or rotated key once. The parent passes `revealedKey`
 * to open the dialog and clears it in `onClose`, so the secret is dropped from
 * state as soon as the dialog closes.
 */
const RawKeyRevealModal = ({ revealedKey, onClose }) => {
  const [isCopied, setIsCopied] = useState(false);
  const { rawKey, serviceName, scopes = [] } = revealedKey ?? {};

  const handleClose = () => {
    setIsCopied(false);
    onClose();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawKey);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard: ", err);
    }
  };

  return (
    <Dialog
      open={!!revealedKey}
      onOpenChange={open => !open && handleClose()}
      disablePointerDismissal
    >
      <DialogContent className="tw:sm:max-w-lg">
        <DialogTitle className="tw:flex tw:items-center tw:gap-2">
          <KeyRound className="tw:size-4" />
          API Key Created
        </DialogTitle>
        <Alert className="tw:border-warning/50 tw:bg-warning/10 tw:text-warning">
          <TriangleAlert />
          <AlertTitle>
            Save this key now &mdash; it cannot be retrieved later.
          </AlertTitle>
          <AlertDescription className="tw:text-foreground">
            This is the only time the full key will be displayed. Store it
            somewhere safe before closing this dialog.
          </AlertDescription>
        </Alert>

        <div className="tw:flex tw:items-stretch tw:gap-2">
          <code className="tw:flex-1 tw:rounded-md tw:border tw:border-border tw:bg-muted tw:p-3 tw:font-mono tw:text-xs tw:break-all tw:text-foreground">
            {rawKey}
          </code>
          <Button
            variant="outline"
            className={cn("tw:h-auto", isCopied && "tw:text-success")}
            onClick={handleCopy}
          >
            {isCopied ? (
              <Check data-icon="inline-start" />
            ) : (
              <Copy data-icon="inline-start" />
            )}
            {isCopied ? "Copied!" : "Copy"}
          </Button>
        </div>

        {(serviceName || scopes.length > 0) && (
          <div className="tw:flex tw:flex-col tw:gap-1">
            {serviceName && (
              <div>
                <strong>Service:</strong> {serviceName}
              </div>
            )}
            {scopes.length > 0 && (
              <div>
                <strong>Scopes:</strong> {scopes.join(", ")}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button onClick={handleClose}>
            I&apos;ve saved it &mdash; close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

RawKeyRevealModal.propTypes = {
  revealedKey: PropTypes.shape({
    rawKey: PropTypes.string.isRequired,
    serviceName: PropTypes.string,
    scopes: PropTypes.arrayOf(PropTypes.string),
  }),
  onClose: PropTypes.func.isRequired,
};

export default RawKeyRevealModal;
